import { useState } from "react"
import { useQuery } from "@tanstack/react-query"

import { golive } from "@/api/golive"

/** Full-screen gate. When the backend reports the site is private (`viewLocked`)
 *  and no valid session is held, nothing renders but the access-code wall — the
 *  app (and all its data requests) mount only once a code has been accepted. The
 *  server enforces this too; the wall is just the UX. */
export function SiteGate({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["whoami"],
    queryFn: golive.whoami,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) return <Splash>Checking access…</Splash>
  if (isError)
    return (
      <Splash>
        <p>Couldn’t reach the server.</p>
        <button onClick={() => refetch()} className="mt-3 rounded-lg bg-header px-3 py-1.5 text-sm font-semibold text-white hover:bg-ink">
          Retry
        </button>
      </Splash>
    )
  if (data?.viewLocked && !data.user) return <LoginWall onUnlocked={() => refetch()} />
  return <>{children}</>
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex h-screen flex-col items-center justify-center gap-2 bg-slate-50 text-center text-slate-500">
      {children}
    </main>
  )
}

function LoginWall({ onUnlocked }: { onUnlocked: () => void }) {
  const [code, setCode] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!code.trim()) return
    setBusy(true)
    setErr(null)
    try {
      await golive.unlock(code.trim()) // stores the session token
      setCode("")
      onUnlocked() // re-checks whoami → renders the app
    } catch (e) {
      setErr((e as Error).message || "That code wasn’t accepted.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-card-hover ring-1 ring-slate-200">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-lg" aria-hidden>
            🔒
          </span>
          <h1 className="font-display text-lg font-bold text-ink">CAT-OS Bugs Report</h1>
        </div>
        <p className="mb-4 text-sm text-slate-500">This site is private. Enter your access code to continue.</p>

        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Access code
        </label>
        <input
          type="password"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-ink outline-none focus:border-headerSub focus:ring-2 focus:ring-headerSub/20"
          placeholder="Your personal code"
        />
        {err && (
          <p className="mt-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 ring-1 ring-red-200">{err}</p>
        )}
        <button
          onClick={submit}
          disabled={busy || !code.trim()}
          className="mt-4 w-full rounded-lg bg-header px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-ink disabled:opacity-50"
        >
          {busy ? "Unlocking…" : "Enter"}
        </button>
        <p className="mt-3 text-center text-[11px] text-slate-400">Access is limited to the CAT-OS team.</p>
      </div>
    </main>
  )
}
